import React, { useState, useEffect } from 'react';
import { apiClient } from '../services/api';
import { useLanguage, useTranslation } from '../i18n';
import type { Meditation } from '../types';
import { MeditationCard } from './MeditationCard';
import './MeditationCatalogPage.css';

export const MeditationCatalogPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [meditations, setMeditations] = useState<Meditation[]>([]);
  const [loading, setLoading] = useState(true);
  const { language } = useLanguage();
  const t = useTranslation();

  // Reload meditations when language, category, or difficulty changes
  useEffect(() => {
    loadMeditations();
  }, [selectedCategory, selectedDifficulty, language]);

  const loadMeditations = async () => {
    try {
      setLoading(true);
      const response = await apiClient.getMeditations(selectedCategory, selectedDifficulty);
      setMeditations(response.data);
    } catch (error) {
      console.error('Failed to load meditations:', error);
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { id: 'all', label: t.common.all },
    { id: 'breathing', label: t.meditation.breathing },
    { id: 'meditation', label: t.meditation.meditationType },
    { id: 'relaxation', label: t.meditation.relaxation },
    { id: 'anxiety', label: t.meditation.anxiety },
    { id: 'sleep', label: t.meditation.sleep }
  ];

  const difficulties = [
    { id: 'all', label: t.meditation.allLevels },
    { id: 'beginner', label: t.meditation.beginner },
    { id: 'intermediate', label: t.meditation.intermediate },
    { id: 'advanced', label: t.meditation.advanced }
  ];


  if (loading) {
    return (
      <div className="page-container meditation-catalog-page">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <p>{t.meditation.loadingMeditations}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container meditation-catalog-page">
      <div className="page-heading-section">
        <h1 className="page-title">{t.meditation.catalog}</h1>
        <p className="page-subtitle">{t.meditation.catalogSubtitle}</p>
      </div>

      <div className="filters-section">
        <div className="filter-group">
          <div className="filter-label">{t.meditation.category}</div>
          <div className="categories-filter">
            {categories.map(category => (
              <button
                key={category.id}
                className={`category-chip ${selectedCategory === category.id ? 'active' : ''}`}
                onClick={() => setSelectedCategory(category.id)}
              >
                {category.label}
              </button>
            ))}
          </div>
        </div>

        <div className="filter-group">
          <div className="filter-label">{t.meditation.difficulty}</div>
          <div className="difficulty-filter">
            {difficulties.map(difficulty => (
              <button
                key={difficulty.id}
                className={`difficulty-chip ${selectedDifficulty === difficulty.id ? 'active' : ''}`}
                onClick={() => setSelectedDifficulty(difficulty.id)}
              >
                {difficulty.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="meditations-grid">
        {meditations.map((meditation) => (
          <MeditationCard key={meditation.id} meditation={meditation} />
        ))}
      </div>

      {meditations.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">🧘</div>
          <h3>{t.meditation.noMeditations}</h3>
          <p>{t.meditation.tryFilters}</p>
        </div>
      )}
    </div>
  );
};
